import React, { useCallback, useEffect, useRef } from "react";
import { connect } from "react-redux";
import FormField from '../../../../../dynamicFormBuilder/FormField';
import { BillingLayout } from '../Layout';

function BillingTab(props) {
    var currentUser = props.oidc?.user;
    const DYNAMICFORMSETTING = props.oidc?.user?.profile?.DYNAMICFORMSETTING;
    const prevBillingMethodRef = useRef(null);
    const prevRateSourceRef = useRef(null);

    const loadLookupClient = useCallback((name, dataSource, valueExpr = 'id', displayExpr = 'text') => {
        if (!props.formRefs?.current?.[name]) return;
        const ref = props.formRefs.current[name];
        if (!ref?.instance) return;

        ref.instance().option('dataSource', dataSource);
        ref.instance().option('valueExpr', valueExpr);
        ref.instance().option('displayExpr', displayExpr);

        const currentValue = props.formFields?.[name];
        if (currentValue !== undefined) {
            ref.instance().option('value', currentValue);
        }
    }, [props.formRefs, props.formFields]);

    // Function to enable/disable fields based on Billing Method
    const updateBillingFieldsState = useCallback((billingMethod) => {
        const billingFields = ['CHARGEBASISUNITS', 'ROUNDING', 'ACCESSBILLAS', 'BILLASMINIMUMQUANTITY', 'CHARGEBASISLEVEL', 'UNITCHARGEFACTOR'];
        
        if (billingMethod === 'F' || billingMethod === 'M') {
            // Disable and clear fields when Billing Method is 'F' (Flat Rate) or 'M' (Manually Entered Quantity)
            billingFields.forEach(fieldName => {
                if (props.formRefs.current && props.formRefs.current[fieldName]) {
                    const ref = props.formRefs.current[fieldName];
                    if (ref && typeof ref.instance === 'function') {
                        const instance = ref.instance();
                        if (instance && typeof instance.option === 'function') {
                            instance.option('disabled', true);
                            if (fieldName === 'BILLASMINIMUMQUANTITY' || fieldName === 'UNITCHARGEFACTOR') {
                                instance.option('value', null);
                            } else {
                                instance.option('value', '');
                            }
                        }
                    }
                }
            });
            
            // Clear values in formFields state only if they're not already null/empty
            props.setformFields(prevFields => {
                const needsUpdate = prevFields.CHARGEBASISUNITS !== '' || 
                                   prevFields.ROUNDING !== '' ||
                                   prevFields.ACCESSBILLAS !== '' ||
                                   prevFields.BILLASMINIMUMQUANTITY !== null ||
                                   prevFields.CHARGEBASISLEVEL !== '' ||
                                   prevFields.UNITCHARGEFACTOR !== null;
                
                if (needsUpdate) {
                    return {
                        ...prevFields,
                        CHARGEBASISUNITS: '',
                        ROUNDING: '',
                        ACCESSBILLAS: '',
                        BILLASMINIMUMQUANTITY: null,
                        CHARGEBASISLEVEL: '',
                        UNITCHARGEFACTOR: null
                    };
                }
                return prevFields;
            });
        } else {
            // Enable fields when Billing Method is not 'F' or 'M'
            billingFields.forEach(fieldName => {
                if (props.formRefs.current && props.formRefs.current[fieldName]) {
                    const ref = props.formRefs.current[fieldName];
                    if (ref && typeof ref.instance === 'function') {
                        const instance = ref.instance();
                        if (instance && typeof instance.option === 'function') {
                            instance.option('disabled', false);
                        }
                    }
                }
            });
        }
    }, [props.formRefs, props.setformFields]);

    // Function to enable/disable rate table fields based on Rate Source
    const updateRateTableFieldsState = useCallback((rateSource) => {
        const rateTableFields = ['R_RATETABLE', 'RATETABLEQUANTITYBASIS', 'CUMULATIVERATE'];
        
        if (rateSource === 'S') {
            // Disable and clear rate table fields when Rate Source is 'S' (Single Rate)
            rateTableFields.forEach(fieldName => {
                if (props.formRefs.current && props.formRefs.current[fieldName]) {
                    const ref = props.formRefs.current[fieldName];
                    if (ref && typeof ref.instance === 'function') {
                        const instance = ref.instance();
                        if (instance && typeof instance.option === 'function') {
                            instance.option('disabled', true);
                            if (fieldName === 'R_RATETABLE') {
                                instance.option('value', null);
                            } else {
                                instance.option('value', '');
                            }
                        }
                    }
                }
            });
            
            // Enable UNITCHARGERATE when Rate Source is 'S' (Single Rate)
            if (props.formRefs.current && props.formRefs.current['UNITCHARGERATE']) {
                const ref = props.formRefs.current['UNITCHARGERATE'];
                if (ref && typeof ref.instance === 'function') {
                    const instance = ref.instance();
                    if (instance && typeof instance.option === 'function') {
                        instance.option('disabled', false);
                    }
                }
            }
            
            // Clear values in formFields state only if they're not already null/empty
            props.setformFields(prevFields => {
                const needsUpdate = prevFields.R_RATETABLE !== null || 
                                   prevFields.RATETABLEQUANTITYBASIS !== '' ||
                                   prevFields.CUMULATIVERATE !== '';
                
                if (needsUpdate) {
                    return {
                        ...prevFields,
                        R_RATETABLE: null,
                        RATETABLEQUANTITYBASIS: '',
                        CUMULATIVERATE: ''
                    };
                }
                return prevFields;
            });
        } else if (rateSource === 'T') {
            // Enable rate table fields when Rate Source is 'T' (Rate Table)
            rateTableFields.forEach(fieldName => {
                if (props.formRefs.current && props.formRefs.current[fieldName]) {
                    const ref = props.formRefs.current[fieldName];
                    if (ref && typeof ref.instance === 'function') {
                        const instance = ref.instance();
                        if (instance && typeof instance.option === 'function') {
                            instance.option('disabled', false);
                        }
                    }
                }
            });
            
            // Disable and clear UNITCHARGERATE when Rate Source is 'T' (Rate Table)
            if (props.formRefs.current && props.formRefs.current['UNITCHARGERATE']) {
                const ref = props.formRefs.current['UNITCHARGERATE'];
                if (ref && typeof ref.instance === 'function') {
                    const instance = ref.instance();
                    if (instance && typeof instance.option === 'function') {
                        instance.option('disabled', true);
                        instance.option('value', null);
                    }
                }
            }
            
            // Clear UNITCHARGERATE value in formFields state only if it's not already null
            props.setformFields(prevFields => {
                if (prevFields.UNITCHARGERATE !== null) {
                    return {
                        ...prevFields,
                        UNITCHARGERATE: null
                    };
                }
                return prevFields;
            });
        } else {
            // Enable all fields when Rate Source is neither 'S' nor 'T'
            rateTableFields.forEach(fieldName => {
                if (props.formRefs.current && props.formRefs.current[fieldName]) {
                    const ref = props.formRefs.current[fieldName];
                    if (ref && typeof ref.instance === 'function') {
                        const instance = ref.instance();
                        if (instance && typeof instance.option === 'function') {
                            instance.option('disabled', false);
                        }
                    }
                }
            });
            
            // Enable UNITCHARGERATE
            if (props.formRefs.current && props.formRefs.current['UNITCHARGERATE']) {
                const ref = props.formRefs.current['UNITCHARGERATE'];
                if (ref && typeof ref.instance === 'function') {
                    const instance = ref.instance();
                    if (instance && typeof instance.option === 'function') {
                        instance.option('disabled', false);
                    }
                }
            }
        }
    }, [props.formRefs, props.setformFields]);

    // Custom handleFieldChange that intercepts BILLINGMETHOD and RATESOURCE changes
    const handleFieldChange = useCallback((name, value) => {
        // Call the parent handleFieldChange first
        props.handleFieldChange(name, value);
        
        // If Billing Method changed, update the billing fields state
        if (name === 'BILLINGMETHOD' && prevBillingMethodRef.current !== value) {
            prevBillingMethodRef.current = value;
            // Use setTimeout to ensure formRefs are ready after state update
            setTimeout(() => {
                updateBillingFieldsState(value);
            }, 0);
        }
        
        // If Rate Source changed, update the rate table fields state
        if (name === 'RATESOURCE' && prevRateSourceRef.current !== value) {
            prevRateSourceRef.current = value;
            // Use setTimeout to ensure formRefs are ready after state update
            setTimeout(() => {
                updateRateTableFieldsState(value);
            }, 0);
        }
    }, [props.handleFieldChange, updateBillingFieldsState, updateRateTableFieldsState]);

    const handleBillingFormLoad = useCallback(() => {
        const statusOptions = [
            { id: 'A', text: 'Active' },
            { id: 'I', text: 'Inactive' },
        ];
        const taxableOptions = [
            { id: 'N', text: 'No' },
            { id: 'Y', text: 'Yes, Apply Tax 1 Only' },
            { id: 'A', text: 'Yes, Apply Tax 2 Only' },
            { id: 'B', text: 'Yes, Apply Both Tax 1 & Tax 2' },
        ];
        const discountableOptions = [
            { id: 'Y', text: 'Yes' },
            { id: 'N', text: 'No' },
        ];
        const billingMethodOptions = [
            { id: 'I', text: 'Item Based' },
            { id: 'F', text: 'Flat Rate' },
            { id: 'M', text: 'Manually Entered Quantity' },
            { id: 'D', text: 'Days (Storage Charge & Bill Behind Only)' },
        ];
        const chargeBasisUnitsOptions = [
            { id: 'A', text: 'Area1' },
            { id: 'E', text: 'Manually Enter The Quantity' },
            { id: 'F', text: 'Flat' },
            { id: 'G', text: 'Gross weight' },
            { id: 'H', text: 'Shipment (eg. Container)' },
            { id: 'I', text: 'Inventory Unit' },
            { id: 'K', text: 'Packages (eg. Pallets)' },
            { id: 'L', text: '# Line items' },
            { id: 'N', text: 'Net Weight' },
            { id: 'P', text: 'Pieces' },
            { id: 'S', text: '# Sublots' },
            { id: 'T', text: 'Amount' },
            { id: 'U', text: 'Value' },
            { id: 'V', text: 'Volume' },
        ];
        const roundingOptions = [
            { id: 'C', text: 'Closest Whole Number' },
            { id: 'D', text: 'Down' },
            { id: 'N', text: 'None' },
            { id: 'U', text: 'Up' },
        ];
        const accessBillAsOptions = [
            { id: 'N', text: 'No, Access In Transaction Entry' },
            { id: 'Y', text: 'Yes, Access In Transaction Entry' },
        ];
        const chargeBasisLevelOptions = [
            { id: 'I', text: 'Item' },
            { id: 'L', text: 'Lot' },
            { id: 'S', text: 'Sub Lot' },
            { id: 'U', text: 'Unit' },
        ];
        const rateSourceOptions = [
            { id: 'S', text: 'Single Rate' },
            { id: 'T', text: 'Rate Table' },
        ];
        const rateTableQuantityBasisOptions = [
            { id: 'C', text: 'Charge Bill As Quantity' },
            { id: 'Q', text: 'Unit Volume' },
            { id: 'U', text: 'Flat Rate' },
        ];
        const cumulativeRateOptions = [
            { id: 'N', text: 'Yes, Rates in Rate Table are Cumulative' },
            { id: 'Y', text: 'No, Rates in Rate Table are not Cumulative' },
        ];

        // Ensure refs are ready after FormField renders
        setTimeout(() => {
            loadLookupClient("STATUS", statusOptions);
            loadLookupClient("TAXABLE", taxableOptions);
            loadLookupClient("DISCOUNTABLE", discountableOptions);
            loadLookupClient("BILLINGMETHOD", billingMethodOptions);
            loadLookupClient("CHARGEBASISUNITS", chargeBasisUnitsOptions);
            loadLookupClient("ROUNDING", roundingOptions);
            loadLookupClient("ACCESSBILLAS", accessBillAsOptions);
            loadLookupClient("CHARGEBASISLEVEL", chargeBasisLevelOptions);
            loadLookupClient("RATESOURCE", rateSourceOptions);
            loadLookupClient("RATETABLEQUANTITYBASIS", rateTableQuantityBasisOptions);
            loadLookupClient("CUMULATIVERATE", cumulativeRateOptions);
            // Set initial state based on current Billing Method value
            const currentBillingMethod = props.formFields?.BILLINGMETHOD;
            if (currentBillingMethod !== undefined) {
                prevBillingMethodRef.current = currentBillingMethod;
                updateBillingFieldsState(currentBillingMethod);
            }
            
            // Set initial state based on current Rate Source value
            const currentRateSource = props.formFields?.RATESOURCE;
            if (currentRateSource !== undefined) {
                prevRateSourceRef.current = currentRateSource;
                updateRateTableFieldsState(currentRateSource);
            }
        }, 100);
    }, [loadLookupClient, props.formFields?.BILLINGMETHOD, props.formFields?.RATESOURCE, updateBillingFieldsState, updateRateTableFieldsState]);

    useEffect(() => {
        const statusOptions = [
            { id: 'A', text: 'Active' },
            { id: 'I', text: 'Inactive' },
        ];
        const taxableOptions = [
            { id: 'N', text: 'No' },
            { id: 'Y', text: 'Yes, Apply Tax 1 Only' },
            { id: 'A', text: 'Yes, Apply Tax 2 Only' },
            { id: 'B', text: 'Yes, Apply Both Tax 1 & Tax 2' },
        ];
        const discountableOptions = [
            { id: 'Y', text: 'Yes' },
            { id: 'N', text: 'No' },
        ];
        const billingMethodOptions = [
            { id: 'I', text: 'Item Based' },
            { id: 'F', text: 'Flat Rate' },
            { id: 'M', text: 'Manually Entered Quantity' },
            { id: 'D', text: 'Days (Storage Charge & Bill Behind Only)' },
        ];
        const chargeBasisUnitsOptions = [
            { id: 'A', text: 'Area1' },
            { id: 'E', text: 'Manually Enter The Quantity' },
            { id: 'F', text: 'Flat' },
            { id: 'G', text: 'Gross weight' },
            { id: 'H', text: 'Shipment (eg. Container)' },
            { id: 'I', text: 'Inventory Unit' },
            { id: 'K', text: 'Packages (eg. Pallets)' },
            { id: 'L', text: '# Line items' },
            { id: 'N', text: 'Net Weight' },
            { id: 'P', text: 'Pieces' },
            { id: 'S', text: '# Sublots' },
            { id: 'T', text: 'Amount' },
            { id: 'U', text: 'Value' },
            { id: 'V', text: 'Volume' },
        ];
        const roundingOptions = [
            { id: 'C', text: 'Closest Whole Number' },
            { id: 'D', text: 'Down' },
            { id: 'N', text: 'None' },
            { id: 'U', text: 'Up' },
        ];
        loadLookupClient("STATUS", statusOptions);
        loadLookupClient("TAXABLE", taxableOptions);
        loadLookupClient("DISCOUNTABLE", discountableOptions);
        loadLookupClient("BILLINGMETHOD", billingMethodOptions);
        loadLookupClient("CHARGEBASISUNITS", chargeBasisUnitsOptions);
        loadLookupClient("ROUNDING", roundingOptions);
        const accessBillAsOptions = [
            { id: 'N', text: 'No, Access In Transaction Entry' },
            { id: 'Y', text: 'Yes, Access In Transaction Entry' },
        ];
        loadLookupClient("ACCESSBILLAS", accessBillAsOptions);
        const chargeBasisLevelOptions = [
            { id: 'I', text: 'Item' },
            { id: 'L', text: 'Lot' },
            { id: 'S', text: 'Sub Lot' },
            { id: 'U', text: 'Unit' },
        ];
        const rateSourceOptions = [
            { id: 'S', text: 'Single Rate' },
            { id: 'T', text: 'Rate Table' },
        ];
        const rateTableQuantityBasisOptions = [
            { id: 'C', text: 'Charge Bill As Quantity' },
            { id: 'Q', text: 'Unit Volume' },
            { id: 'U', text: 'Flat Rate' },
        ];
        const cumulativeRateOptions = [
            { id: 'C', text: 'Charge Bill As Quantity' },
            { id: 'Q', text: 'Unit Volume' },
            { id: 'U', text: 'Flat Rate' },
        ];
        loadLookupClient("CHARGEBASISLEVEL", chargeBasisLevelOptions);
        loadLookupClient("RATESOURCE", rateSourceOptions);
        loadLookupClient("RATETABLEQUANTITYBASIS", rateTableQuantityBasisOptions);
        loadLookupClient("CUMULATIVERATE", cumulativeRateOptions);
        
        // Update fields state when BILLINGMETHOD changes
        const currentBillingMethod = props.formFields?.BILLINGMETHOD;
        if (currentBillingMethod !== undefined && currentBillingMethod !== prevBillingMethodRef.current) {
            prevBillingMethodRef.current = currentBillingMethod;
            updateBillingFieldsState(currentBillingMethod);
        }
        
        // Update fields state when RATESOURCE changes
        const currentRateSource = props.formFields?.RATESOURCE;
        if (currentRateSource !== undefined && currentRateSource !== prevRateSourceRef.current) {
            prevRateSourceRef.current = currentRateSource;
            updateRateTableFieldsState(currentRateSource);
        }
    }, [loadLookupClient, props.formFields?.STATUS, props.formFields?.TAXABLE, props.formFields?.DISCOUNTABLE, props.formFields?.BILLINGMETHOD, props.formFields?.CHARGEBASISUNITS, props.formFields?.ROUNDING, props.formFields?.ACCESSBILLAS, props.formFields?.CHARGEBASISLEVEL, props.formFields?.RATESOURCE, props.formFields?.RATETABLEQUANTITYBASIS, props.formFields?.CUMULATIVERATE, updateBillingFieldsState, updateRateTableFieldsState]);

    return (
        <div>
            <FormField
                currrentUser={currentUser}
                layoutId='7FA92CDE-B3B2-4C36-9966-C5C49E157188'
                layoutName='WMChargeCodeBillingForm'
                defaultFormSchema={BillingLayout}
                formFields={props.formFields}
                setformFields={props.setformFields}
                handleFieldValueChange={handleFieldChange}
                DYNAMICFORMSETTING={DYNAMICFORMSETTING}
                formRefs={props.formRefs}
                onFormLoad={handleBillingFormLoad}
            />
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(BillingTab);

